import { apiClient } from '@/services/core/apiClient';
import type { ApiResponse } from '@/services/core/apiClient';

// Patient-related types
export interface Patient {
  id: string;
  patientClinicDataId: string;
  [key: string]: any;
}

export interface PatientDetails {
  patientClinicDataId: string;
  [key: string]: any;
}

export interface CCMStatusUpdate {
  [key: string]: any;
}

export interface RPMStatusUpdate {
  [key: string]: any;
}

export interface Provider {
  id: string;
  name: string;
  clinicId: string;
  [key: string]: any;
}

export interface DiagnosisCause {
  id: string;
  name: string;
  [key: string]: any;
}

export interface AppointmentFacility {
  id: string;
  name: string;
  clinicId: string;
  [key: string]: any;
}

// Patients API service - TypeScript version of existing patientApi.js
export const patientsApi = {
  /**
   * ES (Virtual Enrollment Specialist) specific endpoints
   */
  
  // Lock patient by group
  async lockPatientByGroup(userId: string, groupId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.post(`/api/pep/${userId}/lockPatientByGroup/${groupId}`);

    if (response.success) {
      console.log('Patient locked by group successfully');
    } else {
      console.error('Failed to lock patient by group:', response.error);
    }

    return response;
  },

  // Transfer patient to CS
  async transferToCs(userId: string, patientId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.post(`/api/pep/${userId}/transferToCs/${patientId}`);

    if (response.success) {
      console.log('Patient transferred to CS successfully');
    } else {
      console.error('Failed to transfer patient to CS:', response.error);
    }

    return response;
  },

  /**
   * EA (Admin) endpoints
   */
  
  // Get EA patients
  async getEaPatients(userId: string): Promise<ApiResponse<Patient[]>> {
    const response = await apiClient.get<Patient[]>(`/api/pep/${userId}/ea`);

    if (response.success) {
      console.log('EA patients fetched:', response.data);
    } else {
      console.error('Failed to get EA patients:', response.error);
    }

    return response;
  },

  // Get EA patient details
  async getEaPatientDetails(userId: string, patientId: string): Promise<ApiResponse<PatientDetails>> {
    const response = await apiClient.get<PatientDetails>(`/api/pep/${userId}/ea/${patientId}`);

    if (response.success) {
      console.log('EA patient details fetched:', response.data);
    } else {
      console.error('Failed to get EA patient details:', response.error);
    }

    return response;
  },

  /**
   * Group management (EA specific)
   */
  
  // Get groups
  async getGroups(userId: string): Promise<ApiResponse<any[]>> {
    const response = await apiClient.get<any[]>(`/api/pep/${userId}/ga`);

    if (response.success) {
      console.log('Groups fetched:', response.data);
    } else {
      console.error('Failed to get groups:', response.error);
    }

    return response;
  },

  // Archive group
  async archiveGroup(userId: string, groupId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.post(`/api/pep/${userId}/archiveGroup/${groupId}`);

    if (response.success) {
      console.log('Group archived successfully');
    } else {
      console.error('Failed to archive group:', response.error);
    }

    return response;
  },

  // Send group details
  async sendGroupDetails(userId: string, groupId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.post(`/api/pep/${userId}/sendGroupDetails/${groupId}`);

    if (response.success) {
      console.log('Group details sent successfully');
    } else {
      console.error('Failed to send group details:', response.error);
    }

    return response;
  },

  /**
   * Additional patient operations
   */
  
  // Invalidate transfer
  async invalidateTransfer(userId: string, transferId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.put(`/api/pep/${userId}/invalidateTransfer/${transferId}`);

    if (response.success) {
      console.log('Transfer invalidated successfully');
    } else {
      console.error('Failed to invalidate transfer:', response.error);
    }

    return response;
  },

  // Unlock patient
  async unlockPatient(userId: string, patientClinicDataId: string): Promise<ApiResponse<any>> {
    const response = await apiClient.put(`/api/pep/${userId}/unlockPatient/${patientClinicDataId}`);

    if (response.success) {
      console.log('Patient unlocked successfully');
    } else {
      console.error('Failed to unlock patient:', response.error);
    }

    return response;
  },

  // Update patient demographic information
  async updatePatientDemographic(userId: string, patientId: number, payload: any, token?: string): Promise<ApiResponse<any>> {
    const response = await apiClient.put(`/api/ngpep/${userId}/patient-demographic/${patientId}`, payload, token);

    if (response.success) {
      console.log('Patient demographic updated successfully');
    } else {
      console.error('Failed to update patient demographic:', response.error);
    }

    return response;
  }
};

export default patientsApi;